import { useState } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import endpoints from '../../config/endpoints';
import { hide } from '../../services/reducers/popupSlice';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import { AppDispatch } from '../../services/store';

interface InventoryAddStockModalProps {
  product: any;
  onSuccess: (updatedProduct: any) => void;
}

// "افزایش موجودی" modal — lets the admin add newly received stock (a number
// of قوطی OR رولی, never an item count) for a single product. The backend
// (AdminInventoryController.addStock) does the actual math and writes the
// matching Cardex ledger row; this component just submits the container
// count and shows the resulting totals back to the admin.
const InventoryAddStockModal = ({ product, onSuccess }: InventoryAddStockModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [unitType, setUnitType] = useState<'box' | 'roll'>('box');
  const [count, setCount] = useState<string>('');

  const itemsPerUnit = unitType === 'box' ? product.itemsPerBox : product.itemsPerRoll;
  const numericCount = Number(count);

  const close = () => dispatch(hide());

  const submit = async () => {
    if (!numericCount || numericCount <= 0) {
      Swal.fire({
        title: 'خطا',
        text: 'تعداد را به درستی وارد کنید',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
      return;
    }

    dispatch(setLoading(true));
    const res = await restApi(endpoints.product.inventory + 'add/' + product.id, true).post({
      unitType,
      count: numericCount
    });
    dispatch(setLoading(false));

    if (res.code === 200) {
      const updated = res.data;
      onSuccess(updated);
      close();
      Swal.fire({
        title: 'موفق',
        html: `موجودی «${product.title}» بروزرسانی شد<br/>` +
          `تعداد کارتن قوطی: ${updated.boxCount} × ${updated.itemsPerBox || '—'}<br/>` +
          `تعداد کارتن رولی: ${updated.rollCount} × ${updated.itemsPerRoll || '—'}<br/>` +
          `خرده تبدیل: ${updated.remains}<br/>` +
          `کل موجودی: ${updated.inventory}`,
        icon: 'success',
        confirmButtonText: 'متوجه شدم'
      });
    } else {
      Swal.fire({
        title: 'ناموفق',
        text: res?.data || 'خطا در بروزرسانی موجودی',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    }
  };

  return (
    <div className="modalContainer" style={{ width: 440, height: 'auto', position: 'static', margin: 0 }}>
      <div className="modalHeading">
        <h2>افزایش موجودی</h2>
        <i className="modalExit clickable" onClick={close}></i>
      </div>

      <div className="modalForm" style={{ flexDirection: 'column' }}>
        <div style={{ width: '100%' }} className="modalInfoLine">
          {product.title} ({product.sku})
        </div>

        <div style={{ width: '100%' }}>
          <label>نوع واحد</label>
          <select
            className="selector"
            style={{ width: '100%', margin: 0 }}
            value={unitType}
            onChange={(e) => setUnitType(e.target.value as 'box' | 'roll')}
          >
            <option value="box">قوطی</option>
            <option value="roll">رولی</option>
          </select>
        </div>

        <div style={{ width: '100%' }}>
          <label>{unitType === 'box' ? 'تعداد کارتن قوطی افزودنی' : 'تعداد کارتن رولی افزودنی'}</label>
          <input
            type="number"
            min="1"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            autoFocus
          />
        </div>

        <div style={{ width: '100%' }} className="modalInfoLine">
          {unitType === 'box' ? 'تعداد در کارتن قوطی' : 'تعداد در کارتن رولی'}: {itemsPerUnit || '—'}
        </div>

        {!!itemsPerUnit && numericCount > 0 && (
          <div style={{ width: '100%' }} className="modalPreviewLine">
            تعداد کل افزوده شده: {numericCount * itemsPerUnit}
          </div>
        )}
      </div>

      <div className="modalBtns">
        <span className="cancel clickable" onClick={close}>انصراف</span>
        <span className="submit clickable" onClick={submit}>ثبت</span>
      </div>
    </div>
  );
};

export default InventoryAddStockModal;
